import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Truck, RotateCcw, Ruler, Package, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import Breadcrumb from '../components/Breadcrumb';
import { ProductCardSkeleton } from '../components/Skeleton';
import ProductCard from '../components/ProductCard';
import ImageZoom from '../components/ImageZoom';
import ShareMenu from '../components/ShareMenu';
import RecentlyViewed from '../components/RecentlyViewed';
import SEO from '../components/SEO';
import { repository } from '../lib/repository';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useRecentlyViewed } from '../contexts/RecentlyViewedContext';
import { formatPrice } from '../lib/utils';

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const { add } = useCart();
  const { has, toggle } = useWishlist();
  const { track } = useRecentlyViewed();

  useEffect(() => {
    repository.getProduct(slug).then((p) => {
      if (!p) { nav('/shop'); return; }
      setProduct(p);
      setSelectedColor(p.colors[0]);
      setSelectedSize(null);
      setActiveImage(0);
      track(p.slug);
      repository.listRelated(p, 4).then(setRelated);
    });
  }, [slug, nav, track]);

  if (!product) return (
    <div className="container py-16" data-testid="pdp-loading">
      <div className="grid gap-8 md:gap-16 md:grid-cols-2">
        <ProductCardSkeleton />
        <div className="space-y-4">
          <div className="h-4 w-24 bg-muted animate-pulse" />
          <div className="h-12 w-3/4 bg-muted animate-pulse" />
          <div className="h-8 w-32 bg-muted animate-pulse" />
        </div>
      </div>
    </div>
  );

  const onSale = product.discount > 0;
  const displayPrice = onSale ? product.salePrice : product.price;

  return (
    <div data-testid={`page-product-${product.slug}`}>
      <SEO
        title={product.seo?.title?.replace(' — Evermark', '') || product.name}
        description={product.seo?.description || product.description}
        path={`/product/${product.slug}`}
        image={product.images[0]}
        type="product"
        keywords={product.seo?.keywords}
        breadcrumb={[
          { to: '/', label: 'Home' },
          { to: '/shop', label: 'Shop' },
          { to: `/shop/${product.category}`, label: product.category.replace(/-/g, ' ') },
          { label: product.name },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          sku: product.sku,
          image: product.images,
          description: product.description,
          brand: { '@type': 'Brand', name: 'Evermark' },
          aggregateRating: product.reviewCount ? {
            '@type': 'AggregateRating',
            ratingValue: product.rating,
            reviewCount: product.reviewCount,
          } : undefined,
          offers: {
            '@type': 'Offer',
            priceCurrency: product.currency,
            price: (product.salePrice || product.price).toString(),
            availability: 'https://schema.org/InStock',
            url: `https://evermark.com/product/${product.slug}`,
          },
          review: (product.reviews || []).slice(0, 3).map((r) => ({
            '@type': 'Review',
            reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
            author: { '@type': 'Person', name: r.author },
            reviewBody: r.body,
            datePublished: r.date,
          })),
        }}
      />
      <div className="container py-8 md:py-12">
        <Breadcrumb items={[
          { to: '/', label: 'Home' },
          { to: '/shop', label: 'Shop' },
          { to: `/shop/${product.category}`, label: product.category.charAt(0).toUpperCase() + product.category.slice(1) },
          { label: product.name },
        ]} />
      </div>

      <div className="container grid gap-8 md:gap-16 md:grid-cols-2">
        {/* Gallery */}
        <div>
          <ImageZoom
            images={product.images}
            activeIndex={activeImage}
            onIndexChange={setActiveImage}
            alt={product.name}
          />
          <div className="mt-4 grid grid-cols-4 gap-2 md:gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                data-testid={`pdp-thumb-${i}`}
                className={`aspect-[3/4] overflow-hidden bg-muted transition-opacity duration-500 ${activeImage === i ? 'opacity-100 ring-1 ring-foreground' : 'opacity-70 hover:opacity-100'}`}
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="md:pl-8 lg:pl-16 md:sticky md:top-32 md:self-start">
          <p className="overline text-muted-foreground">{product.category.replace(/-/g, ' ')} · SKU {product.sku}</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight" data-testid="pdp-name">{product.name}</h1>
          {product.rating > 0 && (
            <div className="mt-3 flex items-center gap-3 text-sm" data-testid="pdp-rating">
              <span className="tabular">{'★'.repeat(Math.round(product.rating))}{'☆'.repeat(5 - Math.round(product.rating))}</span>
              <span className="tabular">{product.rating.toFixed(1)}</span>
              <span className="text-muted-foreground overline">{product.reviewCount} reviews</span>
            </div>
          )}
          <div className="mt-4 flex items-baseline gap-4" data-testid="pdp-price">
            <p className="text-2xl tabular">{formatPrice(displayPrice, product.currency)}</p>
            {onSale && (
              <>
                <p className="text-lg tabular text-muted-foreground line-through">{formatPrice(product.price, product.currency)}</p>
                <span className="overline bg-foreground text-background px-2 py-1">−{product.discount}%</span>
              </>
            )}
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">{product.description}</p>

          {/* Colors */}
          <div className="mt-10">
            <div className="flex items-baseline justify-between">
              <p className="overline">Colour</p>
              <p className="text-sm">{selectedColor?.name}</p>
            </div>
            <div className="mt-3 flex gap-2">
              {product.colors.map((c) => (
                <button
                  key={c.name}
                  aria-label={`Color ${c.name}`}
                  data-testid={`pdp-color-${c.name}`}
                  onClick={() => setSelectedColor(c)}
                  className={`h-9 w-9 rounded-full border transition-transform duration-500 ${selectedColor?.name === c.name ? 'ring-2 ring-offset-2 ring-offset-background ring-foreground scale-105' : 'border-border hover:scale-105'}`}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          </div>

          {/* Sizes */}
          <div className="mt-8">
            <div className="flex items-baseline justify-between">
              <p className="overline">Size</p>
              <Dialog>
                <DialogTrigger className="overline link-underline inline-flex items-center gap-1" data-testid="pdp-size-guide-trigger">
                  <Ruler className="h-3 w-3" /> Size guide
                </DialogTrigger>
                <DialogContent>
                  <h3 className="font-display text-3xl">Size guide</h3>
                  <p className="text-sm text-muted-foreground">Measurements in inches. In between sizes? We suggest sizing down for knitwear, up for outerwear.</p>
                  <table className="w-full text-sm mt-4 tabular">
                    <thead className="text-left overline text-muted-foreground">
                      <tr><th className="py-2">Size</th><th>Chest</th><th>Waist</th><th>Hip</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr><td className="py-2">XS</td><td>34</td><td>26</td><td>34</td></tr>
                      <tr><td className="py-2">S</td><td>36</td><td>28</td><td>36</td></tr>
                      <tr><td className="py-2">M</td><td>38</td><td>30</td><td>38</td></tr>
                      <tr><td className="py-2">L</td><td>40</td><td>32</td><td>40</td></tr>
                      <tr><td className="py-2">XL</td><td>42</td><td>34</td><td>42</td></tr>
                    </tbody>
                  </table>
                </DialogContent>
              </Dialog>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  data-testid={`pdp-size-${s}`}
                  onClick={() => setSelectedSize(s)}
                  className={`h-11 min-w-12 px-4 text-xs tabular border transition-colors duration-500 ${selectedSize === s ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* CTA — wishlist-only mode (no add to cart) */}
          <div className="mt-10">
            <Button
              className="w-full"
              size="lg"
              onClick={() => toggle(product.slug)}
              data-testid="pdp-wishlist-primary"
            >
              <Heart className={`h-4 w-4 mr-1 ${has(product.slug) ? 'fill-current' : ''}`} />
              {has(product.slug) ? 'Saved to wishlist' : 'Add to wishlist'}
            </Button>
            <p className="mt-3 overline text-muted-foreground text-center">
              Waitlist only — we'll write to you when this piece opens for order
            </p>
          </div>

          {/* Icons */}
          <div className="mt-8 grid grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-3"><Truck className="h-4 w-4" /> Free shipping over $150</div>
            <div className="flex items-center gap-3"><RotateCcw className="h-4 w-4" /> 30-day easy returns</div>
            <div className="flex items-center gap-3"><Package className="h-4 w-4" /> Ships from our atelier</div>
            <div className="flex items-center gap-3"><Sparkles className="h-4 w-4" /> Considered, not mass-made</div>
          </div>

          {/* Share */}
          <div className="mt-8 flex items-center justify-between">
            <ShareMenu title={`${product.name} — Evermark`} />
            <p className="overline text-muted-foreground tabular">SKU {product.sku}</p>
          </div>

          {/* Details */}
          <Accordion type="single" collapsible defaultValue="materials" className="mt-10 border-t border-border">
            <AccordionItem value="description">
              <AccordionTrigger data-testid="pdp-description-trigger">Description</AccordionTrigger>
              <AccordionContent>{product.description}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="materials">
              <AccordionTrigger data-testid="pdp-materials-trigger">Materials & Origin</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-1">{product.materials.map((m) => <li key={m}>— {m}</li>)}</ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="specs">
              <AccordionTrigger data-testid="pdp-specs-trigger">Specifications</AccordionTrigger>
              <AccordionContent>
                <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-6">
                  <dt className="overline text-muted-foreground">SKU</dt><dd className="tabular">{product.sku}</dd>
                  <dt className="overline text-muted-foreground">Category</dt><dd className="capitalize">{product.category.replace(/-/g, ' ')}</dd>
                  <dt className="overline text-muted-foreground">Colours</dt><dd>{product.colors.map((c) => c.name).join(' · ')}</dd>
                  <dt className="overline text-muted-foreground">Sizes</dt><dd>{product.sizes.join(' · ')}</dd>
                  {product.season && <><dt className="overline text-muted-foreground">Season</dt><dd className="capitalize">{product.season}</dd></>}
                  <dt className="overline text-muted-foreground">Tags</dt><dd className="capitalize">{product.tags.join(' · ')}</dd>
                </dl>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="care">
              <AccordionTrigger data-testid="pdp-care-trigger">Care</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-1">{product.care.map((m) => <li key={m}>— {m}</li>)}</ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping">
              <AccordionTrigger data-testid="pdp-shipping-trigger">Shipping & Returns</AccordionTrigger>
              <AccordionContent>
                Complimentary shipping on orders over $150 within the US. Free returns within 30 days on unworn pieces.
                International shipping and duties calculated at checkout. See our <Link to="/policies/shipping" className="link-underline">Shipping</Link> and <Link to="/policies/returns" className="link-underline">Returns</Link> policies for the full detail.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Reviews */}
      {product.reviews?.length > 0 && (
        <section data-testid="pdp-reviews" className="container border-t border-border pt-24 md:pt-32 mt-24">
          <div className="grid gap-12 md:grid-cols-[280px_1fr]">
            <div className="md:sticky md:top-32 md:self-start">
              <p className="overline text-muted-foreground">Notes from wearers</p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl leading-[0.95]">Reviews</h2>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-display text-5xl tabular">{product.rating.toFixed(1)}</span>
                <span className="tabular text-muted-foreground">/ 5</span>
              </div>
              <p className="mt-2 overline text-muted-foreground">{product.reviewCount} reviews</p>
              <p className="tabular mt-3">{'★'.repeat(Math.round(product.rating))}{'☆'.repeat(5 - Math.round(product.rating))}</p>
            </div>
            <ul className="space-y-10">
              {product.reviews.map((r) => (
                <li key={r.id} className="border-t border-border pt-8" data-testid={`review-${r.id}`}>
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <span className="tabular text-sm">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                      <span className="text-sm">{r.author}</span>
                      {r.verified && <span className="overline text-muted-foreground">Verified</span>}
                    </div>
                    <span className="overline text-muted-foreground">{new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <h3 className="mt-3 font-display text-2xl">{r.title}</h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{r.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="container py-24 md:py-32">
          <div className="mb-12">
            <p className="overline text-muted-foreground">You may also like</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">Considered Together</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-6">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      <RecentlyViewed excludeSlug={product.slug} title="Recently Viewed" eyebrow="Where you've been" />
    </div>
  );
}
